// hooks/use-collaborate-chat.js
// ── Collaborate (shared-room) chat logic hook ──
//
// Shared-room sibling of hooks/use-workstation-chat.js. Verbatim
// clone in Commit 1. Commit 2 wires observer mode into beforeSend
// so Shift stays silent until Ask Shift is armed (for General Space
// / Professional / Private). Tutor stays active like Solo.
//
// Imports point to the Collaborate clones so Solo is never moved.

import { useRef, useEffect } from 'react';
import { useChatThread } from './use-chat-thread';
import { useArtifacts } from './use-artifacts';
import { FENCE_OPEN_RE } from '../components/collaborate-message';

export function useCollaborateChat({
  userId,
  supabase,
  userRecord,
  workspaceId,
  workspaceSession,
  workspaceGoal,
  messages,
  setMessages,
  pendingFile,
  setPendingFile,
  deepSearchActive,
  setDeepSearchActive,
  deepModeArmed,
  setDeepModeArmed,
  parkedSummary,
  setParkedSummary,
  setParkedAt,
  setLimitWarning,
  setChips,
  setCanvasSuggestion,
  setStaleNodes,
  setLoadingHistory,
  createMomentNode,
  createFileNode,
  filesAtLimit,
  onThinking,
  onArtifactCreated,
  focusedArtifact,
  inputRef,
  voiceResponseCallbackRef,
  // Collaborate-only: observer mode flag + askShift toggle state.
  // observerMode=true => Shift silent by default; askShiftActive
  // drives the next send through the askShift path.
  observerMode = false,
  askShiftActive = false,
}) {
  const rawStreamTextRef = useRef('');
  const fenceStateRef = useRef({ open: false, complete: false });

  const { saveArtifact, detectArtifact } = useArtifacts({ userId, supabase });

  // ── Clear the parked summary ──
  // Removes parked_summary + parked_at from workspace_metadata so the
  // card never re-appears. Called from the card's dismiss × button,
  // the Continue button, and beforeSend (auto-clear on resume).
  async function clearPark() {
    if (!parkedSummary) return;
    setParkedSummary(null);
    setParkedAt(null);
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      await fetch('/api/workspace/park', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authSession?.access_token || ''}`,
        },
        body: JSON.stringify({ workspaceId, action: 'clear' }),
      });
    } catch (e) {
      console.error('[workstation-chat] clear park failed:', e.message);
    }
  }

  const chat = useChatThread({
    messages,
    setMessages,
    typingAnimation: true,
    inputRef,

    beforeSend: async ({ text, isAskShift }) => {
      const fileToSend = pendingFile;
      setPendingFile(null);
      const wasDeepSearch = deepSearchActive;
      if (wasDeepSearch) setDeepSearchActive(false);
      const wasDeepMode = deepModeArmed;
      if (wasDeepMode) setDeepModeArmed(false);
      // Collaborate observer mode: Shift stays silent unless the user
      // explicitly armed Ask Shift. File uploads alone do NOT trigger
      // analysis in observer mode — the file appears in the shared
      // chat for both participants, but Shift waits to be asked. Only
      // Ask Shift or Deep Search forces the AI to weigh in.
      // (Tutor / facilitated rooms have observerMode=false, so Shift
      // continues to respond actively there.)
      const wasAskShift = !!isAskShift;
      const willFireAi = !observerMode || wasAskShift || wasDeepSearch || wasDeepMode;

      // Resuming after a park: the user just sent a new message, so
      // the where-you-left-off card is no longer relevant. Clear it
      // both locally and in the workspace_metadata.
      if (parkedSummary) {
        clearPark();
      }

      const { data: { session: authSession } } = await supabase.auth.getSession();
      const authHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authSession?.access_token || ''}`,
      };

      // ── Upload file to storage (if any) ──
      // Same pattern as chat-space-raw.js: hit /api/user/save-attachment,
      // get a public URL back, save it as an artifact, create a file node
      // in the journey map. This makes the file persist across refreshes
      // (as a marker in the message content) and registers it in the map.
      let filePublicUrl = null;
      let savedArtifact = null;
      if (fileToSend) {
        try {
          const attachRes = await fetch('/api/user/save-attachment', {
            method: 'POST',
            headers: authHeaders,
            body: JSON.stringify({
              sessionId: workspaceId,
              file: {
                base64: fileToSend.base64,
                name: fileToSend.name,
                type: fileToSend.type,
                isImage: fileToSend.isImage,
              },
            }),
          });
          if (attachRes.ok) {
            const attachData = await attachRes.json();
            filePublicUrl = attachData.url || null;
            if (filePublicUrl) {
              try {
                savedArtifact = await saveArtifact(filePublicUrl, 'Upload', workspaceId);
              } catch (e) {
                console.error('[workstation-chat] saveArtifact failed:', e.message);
              }
              // Create a file node in the journey map
              try {
                const result = await createFileNode?.({
                  title: fileToSend.name,
                  artifactId: savedArtifact?.id || null,
                  fileSource: 'uploaded',
                  chatMessageId: null,
                });
                if (result && !result.ok) {
                  setLimitWarning(result.message || 'File limit reached.');
                  setTimeout(() => setLimitWarning(null), 4000);
                } else if (result?.node?.id) {
                  onArtifactCreated?.(result.node.id);
                }
              } catch (e) {
                console.error('[workstation-chat] createFileNode failed:', e.message);
              }
            }
          }
        } catch (e) {
          console.error('[workstation-chat] file upload failed:', e.message);
        }
      }

      // ── Build the message content with file marker ──
      // The marker (e.g., [img:https://...] or [file:https://...]) is what
      // MessageContent renders as a thumbnail. Without this, the file
      // disappears on refresh because the user message text would have
      // no reference to the uploaded file.
      let saveContent = text || '';
      if (fileToSend) {
        const ref = filePublicUrl || fileToSend.name;
        const marker = fileToSend.isImage ? `[img:${ref}]` : `[file:${ref}]`;
        saveContent = saveContent ? `${marker} ${saveContent}` : marker;
      }

      // ── Save user message server-side ──
      let savedMsgId = null;
      try {
        const sendRes = await fetch('/api/space-session', {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            action: 'sendMessage',
            spaceId: workspaceId,
            userId,
            content: saveContent,
          }),
        });
        if (sendRes.ok) {
          const { message: savedMsg } = await sendRes.json();
          savedMsgId = savedMsg?.id || null;
        } else {
          const errBody = await sendRes.text().catch(() => '');
          console.error('[workstation-chat] save user message', sendRes.status, errBody);
        }
      } catch (e) {
        console.error('[workstation-chat] save user message failed:', e.message);
      }

      return {
        userMsgExtras: {
          sender_id: userId,
          ...(savedMsgId ? { id: savedMsgId } : {}),
          // Override the message content with the marker version so the
          // local optimistic render also shows the file thumbnail
          ...(saveContent !== text ? { content: saveContent } : {}),
        },
        fileData: fileToSend ? {
          base64: fileToSend.base64,
          name: fileToSend.name,
          type: fileToSend.type,
          isImage: fileToSend.isImage,
          isPDF: fileToSend.isPDF,
        } : null,
        isDeepSearch: wasDeepSearch,
        isDeepMode: wasDeepMode,
        isAskShift: wasAskShift,
        // Observer mode: message saved + broadcast, but skip the AI
        // fetch entirely. useChatThread honors skipResponse by pushing
        // the user message and returning early.
        skipResponse: !willFireAi,
      };
    },

    buildRequest: async ({ text, fileData, isDeepSearch, isDeepMode, isAskShift, messagesSnapshot }) => {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const authHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authSession?.access_token || ''}`,
      };

      // Observer mode + Ask Shift armed + no file/deepsearch → route
      // through /api/space-session askShift. As of the Collaborate SSE
      // rollout the endpoint streams: tokens flow through onTextToken
      // for live canvas rendering, and the invitee receives the final
      // message through the existing messages-table realtime broadcast.
      const useAskShiftEndpoint = observerMode && isAskShift && !fileData && !isDeepSearch;
      if (useAskShiftEndpoint) {
        return {
          url: '/api/space-session',
          method: 'POST',
          headers: authHeaders,
          body: {
            action: 'askShift',
            spaceId: workspaceId,
            spaceType: workspaceSession?.space_type || 'space',
            profRole: workspaceSession?.prof_role || null,
            participantA: userId,
            participantB: workspaceSession?.others?.[0] || null,
            requestingUser: userId,
            directedQuestion: text,
          },
        };
      }

      // Default path (Tutor/Solo/file/deepsearch) — SSE /api/chat
      return {
        url: '/api/chat',
        method: 'POST',
        headers: authHeaders,
        body: {
          messages: messagesSnapshot.map(m => ({
            role: m.role,
            content: typeof m.content === 'string' ? m.content : '',
          })).filter(m => m.content.trim()),
          userId,
          sessionId: workspaceId,
          aiName: 'Shift', // neutral in collaborate — ignore the user's personalized ai_name
          userName: userRecord?.first_name || null,
          isShared: true,
          askShift: true,
          spaceId: workspaceId,
          spaceType: workspaceSession?.space_type
            || (workspaceSession?.workspace_metadata?.mode === 'solo' ? 'solo' : 'space'),
          ...(isDeepSearch ? { action: 'deepSearch', query: text || ' ' } : {}),
          ...(isDeepMode ? { deepMode: true } : {}),
          ...(fileData ? { fileData } : {}),
          ...(focusedArtifact?.id ? {
            focusedArtifact: {
              id: focusedArtifact.id,
              title: focusedArtifact.title,
              type: focusedArtifact.artifact_type,
              content: (focusedArtifact.content || '').slice(0, 8000),
            },
          } : {}),
        },
      };
    },

    // ── Real-time fence detection via raw token stream ──
    // Typing animation delays m.content, so we track the full raw text
    // here and dispatch canvas events as soon as fences open/close.
    onTextToken: (token) => {
      rawStreamTextRef.current += token;
      const raw = rawStreamTextRef.current;
      const state = fenceStateRef.current;

      // Detect fence opening
      if (!state.open && FENCE_OPEN_RE.test(raw)) {
        state.open = true;
        const type = raw.match(FENCE_OPEN_RE)?.[1] || 'html';
        console.log('[chat-stream] fence OPEN, type:', type);
        window.dispatchEvent(new CustomEvent('workstation:canvas-building', {
          detail: { type },
        }));
      }

      // Stream fenced content to canvas in real-time
      if (state.open && !state.complete) {
        const openMatch = raw.match(FENCE_OPEN_RE);
        if (openMatch) {
          const afterOpen = raw.slice(openMatch.index + openMatch[0].length);
          const closeIdx = afterOpen.indexOf('\n```');

          if (closeIdx >= 0) {
            // Fence closed — send final content
            state.complete = true;
            const fencedContent = afterOpen.slice(0, closeIdx).trim();
            console.log('[chat-stream] fence CLOSED, content length:', fencedContent.length);
            window.dispatchEvent(new CustomEvent('workstation:canvas-streaming', {
              detail: { content: fencedContent, type: openMatch[1] || 'html', done: true },
            }));
          } else {
            // Fence still open — stream partial content to canvas
            // Debounce: only send every ~500ms to avoid hammering the iframe
            const now = Date.now();
            if (!state.lastStreamTime || now - state.lastStreamTime > 500) {
              state.lastStreamTime = now;
              window.dispatchEvent(new CustomEvent('workstation:canvas-streaming', {
                detail: { content: afterOpen, type: openMatch[1] || 'html', done: false },
              }));
            }
          }
        }
      }
    },

    onResponseDone: async (finalText, _userMsg, metadata) => {
      console.log('[onResponseDone] FIRED, finalText length:', finalText?.length || 0);
      // Extract chips from done event metadata
      if (metadata?.chips?.length > 0) {
        setChips(metadata.chips);
      } else {
        setChips([]);
      }
      // Canvas suggestion from Shift — only show if no artifact was built
      // in this response. If a fence was detected, the artifact is already
      // on the canvas and the suggestion is redundant.
      const hadFence = fenceStateRef.current.complete || fenceStateRef.current.open;
      if (metadata?.canvasSuggestion && !hadFence) {
        setCanvasSuggestion(metadata.canvasSuggestion);
      }
      // Reset raw stream tracker
      rawStreamTextRef.current = '';
      fenceStateRef.current = { open: false, complete: false };
      // Clear any building state
      window.dispatchEvent(new CustomEvent('workstation:canvas-building', { detail: { done: true } }));
      // Auto-detect Shift-generated artifacts (HTML, code, etc.)
      const detected = detectArtifact(finalText || '');
      console.log('[onResponseDone] detected:', detected ? { type: detected.type, len: detected.content?.length } : null);
      if (!detected && finalText) {
        const fenceIdx = finalText.indexOf('```');
        console.log('[onResponseDone] NO DETECTION. First fence at idx:', fenceIdx,
          'context:', fenceIdx >= 0 ? finalText.slice(Math.max(0, fenceIdx - 20), fenceIdx + 30) : 'no fence found');
      }
      if (detected) {
        console.log('[onResponseDone] ARTIFACT DETECTED:', {
          type: detected.type,
          contentLen: detected.content?.length,
          focusedArtifactId: focusedArtifact?.id || 'none',
          filesAtLimit,
        });
        // ── Edit-in-place: only if the new content is a partial update.
        const isEditInPlace = focusedArtifact?.id && (() => {
          const oldLen = (focusedArtifact.content || '').length;
          const newLen = (detected.content || '').length;
          // If old artifact is tiny or empty, it's a new creation
          if (oldLen < 50) return false;
          // If lengths differ by more than 60%, it's likely a new document
          const ratio = Math.min(oldLen, newLen) / Math.max(oldLen, newLen);
          return ratio > 0.4;
        })();

        console.log('[onResponseDone] DECISION:', isEditInPlace ? 'EDIT-IN-PLACE' : 'CREATE-NEW');

        if (isEditInPlace) {
          try {
            const { data: { session: authSession } } = await supabase.auth.getSession();
            const res = await fetch('/api/user/artifact-update', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authSession?.access_token || ''}`,
              },
              body: JSON.stringify({
                artifactId: focusedArtifact.id,
                content: detected.content,
              }),
            });
            if (res.ok) {
              window.dispatchEvent(new CustomEvent('workstation:artifact-updated', {
                detail: { artifactId: focusedArtifact.id, content: detected.content },
              }));
            }
          } catch (e) { console.error('[workstation-chat] edit-in-place failed:', e.message); }
        } else if (!filesAtLimit) {
          // ── Create new: no focused artifact, save as a new file
          try {
            console.log('[onResponseDone] saving artifact, type:', detected.type, 'contentLen:', detected.content?.length);
            const saved = await saveArtifact(detected.content, detected.type, workspaceId, { skipDescription: true, messageId: metadata?.assistantMsgId });
            console.log('[onResponseDone] saved:', saved?.id || 'FAILED', saved ? '' : '— check /api/user/save-artifact response');
            if (saved) {
              const result = await createFileNode?.({
                title: saved.title || 'Generated artifact',
                artifactId: saved.id,
                fileSource: 'generated',
                chatMessageId: null,
              });
              if (result && !result.ok) {
                setLimitWarning(result.message || 'File limit reached.');
                setTimeout(() => setLimitWarning(null), 4000);
              } else if (result?.node?.id) {
                console.log('[onResponseDone] focusing node:', result.node.id);
                // Push artifact directly to canvas cache so it doesn't
                // depend on a separate API fetch (which can 401)
                window.dispatchEvent(new CustomEvent('workstation:artifact-loaded', {
                  detail: { artifact: saved },
                }));
                onArtifactCreated?.(result.node.id);
                // Re-focus with increasing delays to handle render cycle timing.
                setTimeout(() => onArtifactCreated?.(result.node.id), 150);
                setTimeout(() => onArtifactCreated?.(result.node.id), 400);
                // Broadcast to journey map via Supabase Realtime
                try {
                  supabase.channel(`journey:${workspaceId}`).send({
                    type: 'broadcast',
                    event: 'node_saved',
                    payload: { nodeId: result.node.id, name: saved.title || 'Artifact' },
                  });
                } catch { /* non-critical */ }
              }
            }
          } catch (e) { console.error('[workstation-chat] artifact save failed:', e.message); }
        }
      }

      // Voice TTS hook for streamed responses
      if (voiceResponseCallbackRef.current) {
        try { voiceResponseCallbackRef.current(finalText || ''); } catch { /* TTS hook failure is non-fatal */ }
      }

      // Chat reference detection — if Shift's response mentions a node title,
      // broadcast it so the journey map can highlight it as 'referenced'
      try {
        const responseText = (finalText || '').toLowerCase();
        const allNodes = window.__shiftWorkspaceNodes || [];
        for (const n of allNodes) {
          if (n.title && n.title.length > 5 && responseText.includes(n.title.toLowerCase())) {
            supabase.channel(`journey:${workspaceId}`).send({
              type: 'broadcast', event: 'chat_reference',
              payload: { nodeId: n.id, timeAgo: 'just now' },
            });
            break; // one reference per response
          }
        }
      } catch { /* non-critical */ }

      // Fire the Haiku moment classifier for the journey map. The lens
      // evaluator is now CRON-BASED (every 6 hours via /api/cron/tick
      // or on-workspace-open via the workstation's own trigger). It no
      // longer fires per-response on the free tier — that's a paid-tier
      // feature for later.
      onThinking?.(true);
      try {
        const { data: { session: authS } } = await supabase.auth.getSession();
        const authHeaders = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authS?.access_token || ''}`,
        };

        // Journey map moment classifier
        try {
          const res = await fetch('/api/workspace/classify-moment', {
            method: 'POST',
            headers: authHeaders,
            body: JSON.stringify({
              shiftResponse: finalText || '',
              goalContext: workspaceGoal || '',
            }),
          });
          if (res.ok) {
            const { type, title } = await res.json();
            if (type && type !== 'none' && title) {
              const momentResult = await createMomentNode?.({ type, title, body: null, chatMessageId: null });
              // Broadcast to journey map
              if (momentResult?.node?.id) {
                try {
                  supabase.channel(`journey:${workspaceId}`).send({
                    type: 'broadcast',
                    event: 'node_created',
                    payload: { nodeId: momentResult.node.id, name: title, nodeType: type },
                  });
                } catch { /* non-critical */ }
              }
            }
          }
        } catch (e) {
          console.error('[workstation-chat] classifier failed:', e.message);
        }
      } finally {
        onThinking?.(false);
      }
    },
  });

  // ── Load existing messages for this workspace ──
  useEffect(() => {
    if (!workspaceId || !supabase) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: { session: authSession } } = await supabase.auth.getSession();
        const res = await fetch(`/api/user/messages?sessionId=${encodeURIComponent(workspaceId)}`, {
          headers: { 'Authorization': `Bearer ${authSession?.access_token || ''}` },
        });
        if (cancelled) return;
        const { messages: loadedMessages } = res.ok ? await res.json() : { messages: [] };
        const loaded = loadedMessages || [];
        setMessages(loaded);
        if (loaded.length === 0 && workspaceGoal) {
          try {
            const { data: { session: authSession2 } } = await supabase.auth.getSession();
            const res2 = await fetch('/api/workspace/opening', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authSession2?.access_token || ''}`,
              },
              body: JSON.stringify({
                workspaceId,
                workspaceGoal,
                aiName: userRecord?.ai_name || 'Shift',
              }),
            });
            if (res2.ok && !cancelled) {
              const { message } = await res2.json();
              if (message) setMessages([message]);
            }
          } catch (e) {
            console.error('[workstation-chat] opening fetch failed:', e.message);
          }
        }
      } catch (e) {
        console.error('[workstation-chat] load messages failed:', e.message);
      } finally {
        if (!cancelled) setLoadingHistory(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line
  }, [workspaceId, supabase, workspaceGoal, userRecord?.ai_name]);

  // ── Health lens: stale node check on workspace open ──
  useEffect(() => {
    if (!workspaceId || !supabase) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: { session: authSession } } = await supabase.auth.getSession();
        const res = await fetch(
          `/api/workspace/health-stale?workspaceId=${encodeURIComponent(workspaceId)}`,
          { headers: { 'Authorization': `Bearer ${authSession?.access_token || ''}` } }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (Array.isArray(data?.nodes) && data.nodes.length > 0) {
          setStaleNodes(data.nodes);
        }
      } catch (e) {
        console.debug('[workstation-chat] health-stale fetch failed:', e.message);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line
  }, [workspaceId, supabase]);

  // ── Acknowledge a stale node ──
  async function handleHealthAck(node, action) {
    setStaleNodes(prev => prev.filter(n => n.id !== node.id));
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      await fetch('/api/workspace/health-acknowledge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authSession?.access_token || ''}`,
        },
        body: JSON.stringify({ nodeId: node.id, action }),
      });
    } catch (e) {
      console.error('[workstation-chat] health-acknowledge failed:', e.message);
    }
    if (action === 'revisit') {
      const prompt = `Take me back to: ${node.title || 'this'}`;
      window.dispatchEvent(new CustomEvent('workstation:fill-chat-input', {
        detail: { text: prompt },
      }));
    }
  }

  // ── Toolbar action: regenerate / retry ─────────────────
  function handleRegenerate(message, chatSend, loading) {
    if (loading || !message) return;
    let userMsgIdx;
    if (message.role === 'assistant') {
      const idx = messages.findIndex(m => m.id === message.id);
      if (idx <= 0) return;
      const prev = messages[idx - 1];
      if (!prev || prev.role !== 'user') return;
      userMsgIdx = idx - 1;
    } else if (message.role === 'user') {
      userMsgIdx = messages.findIndex(m => m.id === message.id);
      if (userMsgIdx < 0) return;
    } else {
      return;
    }
    const userMsg = messages[userMsgIdx];
    const text = typeof userMsg.content === 'string' ? userMsg.content : '';
    if (!text) return;
    const cleanText = text
      .replace(/^\[img:[^\]]+\]\s*/, '')
      .replace(/^\[file:[^\]]+\]\s*/, '');
    setMessages(messages.slice(0, userMsgIdx));
    setTimeout(() => chatSend(cleanText), 0);
  }

  // ── Toolbar action: edit a user message ─────────────────
  function handleEditMessage(message, setInput, inputRef, loading) {
    if (loading || !message || message.role !== 'user') return;
    const text = typeof message.content === 'string' ? message.content : '';
    if (!text) return;
    const idx = messages.findIndex(m => m.id === message.id);
    if (idx < 0) return;
    setMessages(messages.slice(0, idx));
    const cleanText = text
      .replace(/^\[img:[^\]]+\]\s*/, '')
      .replace(/^\[file:[^\]]+\]\s*/, '');
    setInput(cleanText);
    if (inputRef.current) {
      inputRef.current.innerText = cleanText;
      inputRef.current.focus();
      try {
        const range = document.createRange();
        range.selectNodeContents(inputRef.current);
        range.collapse(false);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      } catch { /* selection API may be restricted */ }
    }
  }

  // ── Pin a message as an artifact ────────────────────────
  async function handlePinMessage(message, saveArtifact, loading) {
    const text = typeof message?.content === 'string' ? message.content : '';
    if (!text || loading) return;
    try {
      const saved = await saveArtifact(text, 'Document', workspaceId);
      if (saved) {
        const result = await createFileNode?.({
          title: saved.title || (message.role === 'assistant' ? 'Pinned response' : 'Pinned message'),
          artifactId: saved.id,
          fileSource: 'generated',
          chatMessageId: message.id || null,
        });
        if (result?.node?.id) onArtifactCreated?.(result.node.id);
      }
    } catch (e) {
      console.error('[workstation-chat] pin failed:', e.message);
    }
  }

  return { chat, rawStreamTextRef, fenceStateRef, clearPark, handleHealthAck, handleRegenerate, handleEditMessage, handlePinMessage };
}
